import React from 'react'
import {InputAdornment, Stack, TextField, Typography} from '@mui/material'
import moment from 'moment'
import DatePickerNew from 'src/components-new/inputs/DatePickerNew'
import MultiSelectNew from 'src/components-new/inputs/MultiSelectNew'
import SelectNew from 'src/components-new/inputs/SelectNew'
import TextAreaNew from 'src/components-new/inputs/TextAreaNew'
import TextFieldNew from 'src/components-new/inputs/TextFieldNew'
import TimePickerNew from 'src/components-new/inputs/TimePickerNew'
import {CONTROL_HEIGHT, NEUTRAL_SCALE} from 'src/theme-new/tokens'

// PSP-driven custom field definition — new-style equivalent of the `customFields` array
// consumed by src/components/templates/NewEnrollmentDialog/NewEnrollmentDialog.tsx
// (lines 233-311), sourced from
// `enrollmentHubConfigurations.digitalEnrollmentConfigurations.customFields`.
export interface CustomFieldDef {
  id: string
  label: string
  type: 'string' | 'textarea' | 'number' | 'date' | 'time' | 'enum'
  unit?: string
  options?: string[]
  multiSelect?: boolean
  required?: boolean
}

export type CustomFieldValues = Record<string, any>

/**
 * Numeric input for `type: 'number'` custom fields. TextFieldNew has no numeric variant
 * (only text/email/tel/password — see MaterialRequestDialogNew.tsx for the same
 * workaround), so this mirrors its exact look (external uppercase caption label, same
 * CONTROL_HEIGHT/border/radius) with a plain MUI TextField instead.
 */
function NumberField({label, value, onChange, unit, disabled}: {
  label: string
  value: string
  onChange: (value: string) => void
  unit?: string
  disabled?: boolean
}): JSX.Element {
  return (
    <Stack spacing={0.5}>
      <Typography variant="caption" sx={{fontWeight: 600, color: NEUTRAL_SCALE[700], textTransform: 'uppercase', letterSpacing: '0.05em'}}>
        {label}
      </Typography>
      <TextField
        size="small"
        type="number"
        value={value}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
        fullWidth
        InputProps={unit ? {endAdornment: <InputAdornment position="end">{unit}</InputAdornment>} : undefined}
        sx={{
          '& .MuiOutlinedInput-root': {
            height: CONTROL_HEIGHT,
            borderRadius: '8px',
            backgroundColor: '#fff',
            '& fieldset': {borderColor: NEUTRAL_SCALE[200]},
            '&:hover fieldset': {borderColor: NEUTRAL_SCALE[300]},
          },
        }}
      />
    </Stack>
  )
}

/**
 * Renders one PSP-configured custom field with the *New input matching its `type`,
 * mirroring the legacy switch in NewEnrollmentDialog.tsx lines 234-297:
 * string→TextFieldNew, textarea→TextAreaNew, number→NumberField (+unit), date→DatePickerNew,
 * time→TimePickerNew, enum→MultiSelectNew (multiSelect) or SelectNew, default→TextFieldNew.
 */
export function renderCustomField(
  field: CustomFieldDef,
  value: any,
  onChange: (value: any) => void,
  disabled?: boolean,
): JSX.Element {
  switch (field.type) {
    case 'textarea':
      return (
        <TextAreaNew
          key={field.id}
          label={field.label}
          value={value ?? ''}
          onChange={onChange}
          required={field.required}
          disabled={disabled}
          rows={4}
          fullWidth
        />
      )

    case 'number':
      return (
        <NumberField
          key={field.id}
          label={`${field.label}${field.required ? ' *' : ''}`}
          value={value !== undefined && value !== null ? String(value) : ''}
          onChange={onChange}
          unit={field.unit}
          disabled={disabled}
        />
      )

    case 'date':
      return (
        <DatePickerNew
          key={field.id}
          label={field.label}
          value={value ?? null}
          onChange={onChange}
          required={field.required}
          disabled={disabled}
          fullWidth
        />
      )

    case 'time':
      return (
        <TimePickerNew
          key={field.id}
          label={field.label}
          value={value ?? null}
          onChange={onChange}
          required={field.required}
          disabled={disabled}
          fullWidth
        />
      )

    case 'enum': {
      const options = (field.options ?? []).map(opt => ({value: opt, label: opt}))
      if (field.multiSelect) {
        return (
          <MultiSelectNew
            key={field.id}
            label={field.label}
            value={value ?? []}
            onChange={onChange}
            options={options}
            required={field.required}
            disabled={disabled}
            fullWidth
          />
        )
      }
      return (
        <SelectNew
          key={field.id}
          label={field.label}
          value={value ?? ''}
          onChange={onChange}
          options={options}
          required={field.required}
          disabled={disabled}
          fullWidth
        />
      )
    }

    case 'string':
    default:
      return (
        <TextFieldNew
          key={field.id}
          label={field.label}
          value={value ?? ''}
          onChange={onChange}
          required={field.required}
          disabled={disabled}
          fullWidth
        />
      )
  }
}

/**
 * Builds the `customFields` array sent to the backend — identical shape to the legacy
 * `customFieldsData` in NewEnrollmentDialog.tsx lines 57-67 — dropping any field left
 * empty.
 */
export function collectCustomFields(fields: CustomFieldDef[], values: CustomFieldValues): Array<{
  id: string
  value: any
  label: string
  type: string
  unitType?: string
}> {
  return fields
    .map((field) => ({
      id: field.id,
      value: values[field.id],
      label: field.label,
      type: field.type,
      ...(field.unit && {unitType: field.unit}),
    }))
    .filter((field) => field.value !== undefined && field.value !== null && field.value !== '' &&
      !(Array.isArray(field.value) && field.value.length === 0))
}

/** Read-only rendering of one custom field's value, for the detail dialog. */
export function formatCustomFieldValue(field: CustomFieldDef, value: any): string {
  if (value === undefined || value === null || value === '') return '—'
  if (field.type === 'date') return moment(value).format('DD/MM/YYYY')
  if (field.type === 'time') return moment(value).format('HH:mm')
  if (field.type === 'number') return field.unit ? `${value} ${field.unit}` : String(value)
  if (Array.isArray(value)) return value.join(', ')
  return String(value)
}
